import { useEffect, useMemo, useState } from 'react';
import { api, resolveMediaUrl } from '../../../apis/api';
import { AdminFileUpload } from '../../components/fileUpload/fileUpload';
import '../orders/orders.css';
import '../categories/categories.css';
import './products.css';

const emptyForm = {
  product_name: '',
  category_id: '',
  price: '',
  discount_price: '',
  youtube_link: '',
  unit: 'BOX',
  status: 1,
  sortorder: '',
};

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catFilter, setCatFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [prodRes, catRes] = await Promise.all([
        api.getProducts({ all: 1 }),
        api.getCategories({ all: 1 }),
      ]);
      setProducts(prodRes.data || []);
      setCategories(catRes.data || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const filtered = useMemo(() => {
    let list = products;
    if (catFilter !== 'All') {
      list = list.filter((p) => String(p.category_id) === String(catFilter));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          (p.product_name || '').toLowerCase().includes(q) ||
          (p.category_name || '').toLowerCase().includes(q) ||
          String(p.product_id).includes(q)
      );
    }
    return [...list].sort(
      (a, b) => (Number(a.sortorder) - Number(b.sortorder)) || (a.product_id - b.product_id)
    );
  }, [products, catFilter, search]);

  const resetImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview('');
  };

  const openAdd = () => {
    setModalMode('add');
    setEditing(null);
    setForm({
      ...emptyForm,
      category_id: categories[0]?.category_id || '',
    });
    resetImage();
  };

  const openEdit = (p) => {
    setModalMode('edit');
    setEditing(p);
    setForm({
      product_name: p.product_name || '',
      category_id: p.category_id,
      price: p.price,
      discount_price: p.discount_price,
      youtube_link: p.youtube_link || '',
      unit: p.unit || 'BOX',
      status: Number(p.status) === 0 ? 0 : 1,
      sortorder: p.sortorder ?? '',
    });
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview(p.product_image ? resolveMediaUrl(p.product_image) : '');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditing(null);
    setForm(emptyForm);
    resetImage();
  };

  const onImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const save = async () => {
    const product_name = String(form.product_name || '').trim();
    if (!product_name) {
      alert('Product name is required');
      return;
    }
    if (!form.category_id) {
      alert('Category is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        product_name,
        category_id: Number(form.category_id),
        price: Number(form.price),
        discount_price: Number(form.discount_price),
        youtube_link: form.youtube_link || '',
        unit: form.unit || 'BOX',
        status: Number(form.status) === 1 ? 1 : 0,
        sortorder: form.sortorder !== '' ? Number(form.sortorder) : undefined,
        imageFile: imageFile || undefined,
      };
      if (modalMode === 'add') {
        await api.createProduct(payload);
      } else if (editing) {
        await api.updateProduct(editing.product_id, payload);
      }
      closeModal();
      await load();
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="admin-loading">Loading products…</p>;
  if (error) return <p className="admin-error">{error}</p>;

  return (
    <div className="admin-products">
      <div className="admin-products__topbar">
        <div className="admin-products__filters">
          <select className="admin-select" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
            <option value="All">All Categories</option>
            {categories.map((c) => (
              <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
            ))}
          </select>
          <input
            className="admin-products__search"
            type="search"
            placeholder="Filter products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Filter products"
          />
        </div>
        <div className="admin-products__topbar-right">
          {/* <span className="admin-products__count">{filtered.length} products</span> */}
          <button type="button" className="admin-btn-primary" onClick={openAdd}>
            Add Product
          </button>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>SORT</th>
              <th>IMAGE</th>
              <th>NAME</th>
              <th>CATEGORY</th>
              <th>PRICE</th>
              <th>DISCOUNT</th>
              <th>% OFF</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  {products.length === 0 ? 'No products yet. Add one to get started.' : 'No matching products'}
                </td>
              </tr>
            ) : (
              filtered.map((p) => (
                <tr key={p.product_id}>
                  <td>{p.sortorder ?? '—'}</td>
                  <td>
                    {p.product_image ? (
                      <img
                        src={resolveMediaUrl(p.product_image)}
                        alt={p.product_name}
                        className="admin-products__img"
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{p.product_name}</td>
                  <td>{p.category_name}</td>
                  <td>₹{p.price}</td>
                  <td>₹{p.discount_price}</td>
                  <td>{Math.round(p.discount_percentage || 0)}%</td>
                  <td>
                    <span
                      className={`status-badge ${
                        Number(p.status) === 1 ? 'status--active' : 'status--inactive'
                      }`}
                    >
                      {Number(p.status) === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="admin-table__actions">
                    <button type="button" className="admin-btn admin-btn--ghost" onClick={() => openEdit(p)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalMode && (
        <div className="admin-modal-backdrop" onClick={closeModal}>
          <div className="admin-modal admin-modal--wide" onClick={(e) => e.stopPropagation()}>
            <h3>
              {modalMode === 'add'
                ? 'Add Product'
                : `Edit Product #${editing?.product_id}`}
            </h3>
            <label>
              Name
              <input
                value={form.product_name}
                onChange={(e) => setForm((f) => ({ ...f, product_name: e.target.value }))}
                placeholder="Product name"
                autoFocus
              />
            </label>
            <label>
              Category
              <select
                value={form.category_id}
                onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
              >
                {categories.length === 0 ? (
                  <option value="">No categories</option>
                ) : (
                  categories.map((c) => (
                    <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
                  ))
                )}
              </select>
            </label>
            <div className="admin-products__row">
              <label>
                Price
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                />
              </label>
              <label>
                Discount Price
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.discount_price}
                  onChange={(e) => setForm((f) => ({ ...f, discount_price: e.target.value }))}
                />
              </label>
            </div>
            <div className="admin-products__row">
              <label>
                Sort Order
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.sortorder}
                  onChange={(e) => setForm((f) => ({ ...f, sortorder: e.target.value }))}
                  placeholder="Display position (1 = first)"
                />
              </label>
              <label>
                Unit
                <input
                  value={form.unit}
                  onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                  placeholder="BOX"
                />
              </label>
            </div>
            <div className="admin-products__row">
              <label>
                Status
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: Number(e.target.value) }))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </label>
            </div>
            <AdminFileUpload
              id="product-image-upload"
              label="Image"
              accept="image/*"
              fileName={imageFile?.name || ''}
              onChange={onImageChange}
            />
            {imagePreview ? (
              <div className="admin-products__preview">
                <img src={imagePreview} alt="Preview" />
              </div>
            ) : null}
            <label>
              YouTube Link
              <input
                value={form.youtube_link}
                onChange={(e) => setForm((f) => ({ ...f, youtube_link: e.target.value }))}
                placeholder="https://youtube.com/…"
              />
            </label>
            <div className="admin-modal__actions">
              <button type="button" className="admin-btn admin-btn--ghost" onClick={closeModal}>
                Cancel
              </button>
              <button type="button" className="admin-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : modalMode === 'add' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
